import { Card, Typography } from '@/components/atoms';
import { useTranslation, useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { getShadowStyle } from '@/core/utils/shadowStyles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, LayoutChangeEvent, PanResponder, StyleSheet, View } from 'react-native';
import { SliderEndLabel, SliderTickMarks, TICK_MARKS_RESERVED_HEIGHT } from './SliderDecorations';

const THUMB_SIZE = 16;
const TRACK_HEIGHT = 5;

export interface PriceRangeSliderProps {
    min?: number;
    max?: number;
    value?: number;
    ticks?: number[]; // major, labeled ticks
    minorTickStep?: number; // interval for the small unlabeled ruler ticks between major ticks
    unit?: string;
    onValueChange?: (value: number) => void;
    /**
     * Sanctioned Iskan loan in AED (same as web's PriceRangeSlider) — shown
     * fixed in the header, independent of the thumb position. Does NOT change
     * while dragging, unlike the (thumb-position-driven) `value`.
     */
    loanAmountAed?: number;
}

const clamp = (n: number, lo: number, hi: number) => Math.min(Math.max(n, lo), hi);

// AC-06: whole numbers show no decimals, otherwise round to (at most) 2 decimal places
// and drop trailing zeros — 2 -> "2", 2.5 -> "2.5", 2.25 -> "2.25", 2.257 -> "2.26".
const formatLoanAmount = (value: number): string => {
    const rounded = Math.round(value * 100) / 100;
    return Number(rounded.toFixed(2)).toString();
};

export function PriceRangeSlider({
    min = 1,
    max = 6,
    value: initialValue = 2.0,
    ticks = [2, 3, 4, 5],
    minorTickStep = 0.25,
    unit,
    onValueChange,
    loanAmountAed,
}: Readonly<PriceRangeSliderProps>) {
    const colors = useUIColors();
    const { t } = useTranslation();
    const { isRTL } = useI18nContext();
    // Default kept in sync with the locale (t()'s value isn't available in a
    // parameter default) — callers can still pass a literal unit to override.
    const displayUnit = unit ?? t('properties.millionAbbrev');
    const [value, setValue] = useState(clamp(initialValue, min, max));
    // Fixed header display — same as web: doesn't move while dragging. Falls
    // back to the thumb position only if no loanAmountAed was passed in.
    const loanAmountDisplayMillions =
        loanAmountAed != null ? loanAmountAed / 1_000_000 : initialValue;
    const [trackWidth, setTrackWidth] = useState(0);
    const trackWidthRef = useRef(0);

    // Sync initialValue prop changes into local state
    useEffect(() => {
        setValue(clamp(initialValue, min, max));
    }, [initialValue, min, max]);

    // RTL: touch x is still a physical (left-origin) coordinate — PanResponder's
    // locationX is never mirrored — but "min" now sits at the physical right
    // edge, so the fraction filled runs from that edge inward instead of from 0.
    const positionToValue = (x: number) => {
        const width = trackWidthRef.current;
        if (width <= 0) return min;
        const fillLength = isRTL ? width - x : x;
        const ratio = clamp(fillLength / width, 0, 1);
        return min + ratio * (max - min);
    };

    // Physical (left-origin) x for a given value — used for the thumb's
    // translateX and the tick marks' `left`, so both stay correctly mirrored
    // together. Kept anchored to direction:'ltr' on trackTouchArea below so
    // native RTL auto-mirroring of `left`/`right` can't double-flip this.
    const valueToPosition = (v: number, width: number) => {
        if (width <= 0) return 0;
        const ratio = (clamp(v, min, max) - min) / (max - min);
        const fillLength = ratio * width;
        return isRTL ? width - fillLength : fillLength;
    };

    // Drives the thumb/fill directly via the Animated bridge on every touch-move
    // (no React re-render per pixel) — `value` (React state, used for the tick
    // highlight and committed on release) only updates at most once per frame
    // via requestAnimationFrame, since re-rendering the whole card on every raw
    // touch-move event was the actual cause of the jank on physical devices.
    const animatedThumbX = useRef(new Animated.Value(valueToPosition(clamp(initialValue, min, max), 0))).current;
    const rafIdRef = useRef<number | null>(null);
    const pendingXRef = useRef<number | null>(null);

    const commitPendingFrame = () => {
        rafIdRef.current = null;
        if (pendingXRef.current == null) return;
        setValue(positionToValue(pendingXRef.current));
    };

    const handleDragMove = (x: number) => {
        const clampedX = clamp(x, 0, trackWidthRef.current);
        animatedThumbX.setValue(clampedX);
        pendingXRef.current = x;
        rafIdRef.current ??= requestAnimationFrame(commitPendingFrame);
    };

    const handleDragEnd = (x: number) => {
        if (rafIdRef.current != null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
        pendingXRef.current = null;
        const newValue = positionToValue(x);
        setValue(newValue);
        onValueChange?.(newValue);
    };

    // PanResponder.create() below only runs once (ref), so it would otherwise
    // freeze whichever handleDragMove/handleDragEnd closures existed on the
    // first render — same fix the original code applied via a ref for this
    // exact reason, needed here too since handleDragEnd closes over the
    // onValueChange prop.
    const handleDragMoveRef = useRef(handleDragMove);
    const handleDragEndRef = useRef(handleDragEnd);
    useEffect(() => {
        handleDragMoveRef.current = handleDragMove;
        handleDragEndRef.current = handleDragEnd;
    });

    // evt.nativeEvent.locationX is recomputed relative to the touched view on every
    // single move event, which is a known source of jitter during fast/continuous
    // drags (especially on Android). Capture the starting position once on grant,
    // then track movement via gestureState.dx — the delta RN computes directly from
    // the raw touch stream — instead of re-deriving locationX on every move.
    const dragStartXRef = useRef(0);

    const panResponder = useRef(
        PanResponder.create({
            onStartShouldSetPanResponder: () => true,
            onMoveShouldSetPanResponder: () => true,
            // Same as web's PriceRangeSlider — once a drag has started, refuse to
            // hand the responder to any other view (e.g. a parent ScrollView) so
            // a mid-swipe hiccup can't abruptly cut the drag short.
            onPanResponderTerminationRequest: () => false,
            onPanResponderGrant: (evt) => {
                dragStartXRef.current = evt.nativeEvent.locationX;
                handleDragMoveRef.current(dragStartXRef.current);
            },
            onPanResponderMove: (evt, gestureState) =>
                handleDragMoveRef.current(dragStartXRef.current + gestureState.dx),
            onPanResponderRelease: (evt, gestureState) =>
                handleDragEndRef.current(dragStartXRef.current + gestureState.dx),
        }),
    ).current;

    useEffect(() => {
        return () => {
            if (rafIdRef.current != null) {
                cancelAnimationFrame(rafIdRef.current);
            }
        };
    }, []);

    const handleTrackLayout = (e: LayoutChangeEvent) => {
        const width = e.nativeEvent.layout.width;
        trackWidthRef.current = width;
        setTrackWidth(width);
    };

    const thumbPosition = valueToPosition(value, trackWidth);

    // Benchmark marker — fixed red line on the track at the sanctioned loan
    // amount's position, independent of the thumb, so the user can see where
    // their loan sits relative to the selected price without it moving as
    // they drag (unlike the thumb, which tracks `value`).
    const loanMarkerPosition =
        trackWidth > 0 && loanAmountAed != null
            ? valueToPosition(loanAmountAed / 1_000_000, trackWidth)
            : null;

    // Keep the Animated position in sync whenever the value changes from
    // something OTHER than dragging (initial mount, prop change, track layout
    // resolving) — while actively dragging, handleDragMove already drives it.
    useEffect(() => {
        animatedThumbX.setValue(thumbPosition);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thumbPosition]);

    // Small unlabeled ruler ticks between the major ticks (real proportional position).
    const minorTicks = useMemo(() => {
        if (minorTickStep <= 0) return [];
        const result: number[] = [];
        for (let v = min + minorTickStep; v < max; v += minorTickStep) {
            const rounded = Math.round(v * 1000) / 1000;
            if (ticks.some((t) => Math.abs(t - rounded) < 0.01)) continue;
            result.push(rounded);
        }
        return result;
    }, [min, max, minorTickStep, ticks]);

    const nearestTick = [...ticks, ...minorTicks].reduce<number | undefined>((closest, tick) => {
        if (closest === undefined) return tick;
        return Math.abs(tick - value) < Math.abs(closest - value) ? tick : closest;
    }, undefined);

    // Stable identity across renders (only changes when its real inputs do) so
    // SliderTickMarks' React.memo can actually skip re-rendering its ~16 tick
    // views on every RAF-driven `value` update while dragging — otherwise a new
    // inline function here would defeat the memo on every single frame.
    const getTickPosition = useCallback(
        (v: number) => valueToPosition(v, trackWidth),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [trackWidth, min, max, isRTL],
    );

    return (
        <Card
            variant="default"
            style={[
                styles.card,
                getShadowStyle({ ...styles.cardShadow, shadowColor: colors.fontPrimary }),
                {
                    backgroundColor: colors.backgroundLevel1,
                },
            ]}
        >
            <View style={styles.headerRow}>
                <Typography color="bloomRedDark" style={styles.title}>
                    {t('properties.priceRange')}
                </Typography>
                <Typography variant="caption" color="tertiary">
                    <Typography variant="caption" color="primary" style={styles.loanAmountValue}>
                        {formatLoanAmount(loanAmountDisplayMillions)}{displayUnit}
                    </Typography>
                    {' '}
                    {t('properties.loanAmount')}
                </Typography>
            </View>

            <View style={styles.sliderBlock}>
                <View style={[styles.trackRow, isRTL && styles.trackRowRTL]}>
                    <SliderEndLabel label={`${min}${displayUnit}`} />

                    <View
                        style={[styles.trackTouchArea, isRTL && styles.trackTouchAreaRTL]}
                        onLayout={handleTrackLayout}
                        // The visual track is only TRACK_HEIGHT (5px) tall — far
                        // smaller than a comfortable tap target — so a finger
                        // landing a little above/below the line wouldn't even
                        // start the drag. hitSlop expands where touches are
                        // recognized without affecting layout/child positioning.
                        hitSlop={{ top: 20, bottom: 20 }}
                        {...panResponder.panHandlers}
                    >
                        <View style={[styles.track, { backgroundColor: colors.backgroundLevel3 }]}>
                            <Animated.View
                                style={[
                                    styles.fill,
                                    isRTL ? styles.fillRTL : null,
                                    {
                                        // animatedThumbX is the thumb's physical (left-origin) x — in
                                        // RTL that's W at "min" and 0 at "max", so the right-anchored
                                        // fill's width is the remainder, W - x (0 at min, W at max).
                                        width: isRTL
                                            ? Animated.subtract(trackWidth, animatedThumbX)
                                            : animatedThumbX,
                                        backgroundColor: colors.bloomRedDark,
                                    },
                                ]}
                                pointerEvents="none"
                            />
                        </View>
                        <Animated.View
                            style={[
                                styles.thumb,
                                {
                                    left: -THUMB_SIZE / 2,
                                    transform: [{ translateX: animatedThumbX }],
                                    backgroundColor: colors.bloomRedDark,
                                    borderColor: colors.backgroundLevel1,
                                },
                            ]}
                            pointerEvents="none"
                        />

                        {loanMarkerPosition != null && (
                            <View
                                style={[
                                    styles.loanMarker,
                                    { left: loanMarkerPosition, backgroundColor: colors.bloomRedDark },
                                ]}
                                pointerEvents="none"
                            />
                        )}

                        {/* Ruler-style tick marks: small unlabeled lines at regular intervals,
                            plus labeled major ticks - all at their real proportional position. */}
                        <View style={styles.tickMarksContainer} pointerEvents="none">
                            <SliderTickMarks
                                minorTicks={minorTicks}
                                majorTicks={ticks}
                                nearestTick={nearestTick}
                                unit={displayUnit}
                                getPosition={getTickPosition}
                            />
                        </View>
                    </View>

                    <SliderEndLabel label={`${max.toFixed(1)} ${displayUnit} +`} />
                </View>

                {/* Reserves vertical space for the absolutely-positioned tick marks/labels. */}
                <View style={styles.tickMarksSpacer} />
            </View>
        </Card>
    );
}

const styles = StyleSheet.create({
    card: {
        padding: 12,
    },
    cardShadow: {
        shadowOffset: {
            width: 0,
            height: 4,
        },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 8,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    title: {
        fontFamily: 'Matter-SemiBold',
        fontWeight: '600',
        fontSize: 12,
        lineHeight: 16,
        letterSpacing: 0,
        marginBottom: 9,
    },
    loanAmountValue: {
        fontFamily: 'Matter-SemiBold',
        lineHeight: 16,
        letterSpacing: 0,
    },
    sliderBlock: {
        gap: 0,
    },
    trackRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
    },
    // direction:'ltr' + explicit row-reverse (not left to native auto-mirror)
    // puts the min label on the physical right and max label on the physical
    // left, matching the mirrored track math above.
    trackRowRTL: {
        direction: 'ltr',
        flexDirection: 'row-reverse',
    },
    trackTouchArea: {
        flex: 1,
    },
    // direction:'ltr' makes every `left`/`right` and `transform` inside this
    // box immune to native RTL auto-mirroring — position math below (isRTL
    // branches in positionToValue/valueToPosition, fillRTL) is fully explicit
    // instead, same pattern used for Accordion/LanguageSwitch elsewhere.
    trackTouchAreaRTL: {
        direction: 'ltr',
    },
    track: {
        height: TRACK_HEIGHT,
        borderRadius: TRACK_HEIGHT / 2,
        overflow: 'hidden',
    },
    fill: {
        height: '100%',
        borderRadius: TRACK_HEIGHT / 5,
    },
    // RTL: "min" is at the physical right edge, so the fill must grow from
    // the right toward the thumb instead of from the left.
    fillRTL: {
        position: 'absolute',
        top: 0,
        right: 0,
    },
    thumb: {
        position: 'absolute',
        top: (TRACK_HEIGHT - THUMB_SIZE) / 2,
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        borderRadius: THUMB_SIZE / 2,
        borderWidth: 2,
    },
    // Fixed benchmark line at the loan amount's position — taller than the
    // track so it's visible above/below the fill regardless of thumb position.
    loanMarker: {
        position: 'absolute',
        width: 2,
        height: TRACK_HEIGHT + 8,
        borderRadius: 1,
    },
    tickMarksContainer: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: TRACK_HEIGHT,
    },
    tickMarksSpacer: {
        height: TICK_MARKS_RESERVED_HEIGHT,
    },
});

export default PriceRangeSlider;
